import { CategoriaGastoBadge } from '@/components/gastos/CategoriaGastoBadge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useGastosMensuales } from '@/hooks/useGastos'
import type { PeriodoComparado } from '@/lib/periodo-reporte'
import { formatCurrency } from '@/lib/utils'
import { egresosPorCategoria } from './egresos'

/**
 * Gastos del período en pesos por categoría, de mayor a menor, sin retiros.
 * Se monta solo con verCuentas, porque montarlo dispara la consulta de gastos.
 * Pide el mismo rango que KpiEgresos, así las dos tarjetas comparten la consulta.
 */
export function ComposicionGastos({ periodo }: { periodo: PeriodoComparado }) {
  const { data: rows, isLoading, isError } = useGastosMensuales(periodo.anterior.desde.slice(0, 7))
  const categorias = rows ? egresosPorCategoria(rows, periodo.actual) : []
  const total = categorias.reduce((acc, c) => acc + c.total, 0)

  return (
    <Card role="region" aria-labelledby="composicion-gastos-titulo">
      <CardHeader className="pb-2">
        <CardTitle id="composicion-gastos-titulo" className="text-sm font-medium text-muted-foreground">
          Composición de gastos
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isError ? (
          <p className="text-sm text-destructive">No se pudo cargar el dato.</p>
        ) : isLoading || !rows ? (
          <Skeleton className="h-24 w-full" />
        ) : categorias.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin gastos en el período.</p>
        ) : (
          categorias.map((c) => {
            const pct = total > 0 ? Math.round((c.total / total) * 100) : 0
            return (
              <div key={c.clave} data-testid="categoria-gasto" className="flex items-center gap-2">
                <CategoriaGastoBadge nombre={c.categoria} color={c.color} className="w-28 shrink-0 truncate" />
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-28 whitespace-nowrap text-right text-sm font-medium">{formatCurrency(c.total)}</span>
                <span className="w-10 text-right text-xs text-muted-foreground">{pct}%</span>
              </div>
            )
          })
        )}
      </CardContent>
    </Card>
  )
}
